import React, { useState, useRef } from "react";
import { Container, SearchContainer, SearchBar, ChipContainer } from "./styled";
import Button from "../../ReUsableComponents/Button";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { makeStyles } from "@mui/styles";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { useDispatch, useSelector } from "react-redux";
import { openAlert } from "../../../../../../Redux/Slices/alertSlice";
import { boardMemberAdd, boardMemberRemove } from "../../../../../../Services/boardService";
import { useSearchUser } from "../../../../../common/Nav";
import { useAppContext } from "../../../../../../context/useContext";
import useOnClickOutside from "../../../../../../hooks/useOnClickOutside";
import ReactLoading from "react-loading";

const useStyles = makeStyles({
  root: {
    maxWidth: "8rem",
    opacity: "70%",
  },
});

// Chip cho member gốc (có thể xóa khỏi board)
const OriginalMemberChip = (props) => {
  const { name, callback, image, role, user } = props;
  const classes = useStyles();
  // Không cho phép xóa admin
  const canDelete = role !== "owner";
  
  return (
    <Tooltip
      TransitionComponent={Zoom}
      title={
        role === "owner" 
          ? `${name} (Chủ sở hữu - Không thể xóa)`
          : `${name} (${role === "owner" ? "Chủ sở hữu" : "Thành viên"})`
      }
      size="small"
      placement="top"
      arrow
    >
      <Chip
        className={classes.root}
        onDelete={canDelete ? () => callback(user) : undefined}
        avatar={image?.url ? <Avatar src={image.url} /> : <Avatar>{name.toString()[0]}</Avatar>}
        label={name}
        size="small"
        color={role === "owner" ? "primary" : "secondary"}
        style={!canDelete ? { opacity: 0.8 } : {}}
      />
    </Tooltip>
  );
};

// Chip cho member mới (cần add, có thể xóa khỏi danh sách chờ)
const NewMemberChip = (props) => {
  const { name, callback, image, email } = props;
  const classes = useStyles();
  return (
    <Tooltip
      TransitionComponent={Zoom}
      title={`${name} - Sẽ được thêm vào bảng`}
      size="small"
      placement="top"
      arrow
    >
      <Chip
        className={classes.root}
        onDelete={() => callback(email)}
        avatar={image?.url ? <Avatar src={image.url} /> : <Avatar>{name.toString()[0]}</Avatar>}
        label={name}
        size="small"
        color="default"
        style={{ backgroundColor: "#e3f2fd", border: "1px dashed #1976d2" }}
      />
    </Tooltip>
  );
};

const InviteMembers = () => {
  const dispatch = useDispatch();
  const boardMembers = useSelector((state) => state.board.members);
  // Danh sách member mới cần add (tách biệt với member gốc)
  const [newMembers, setNewMembers] = useState([]);
  const boardId = useSelector((state) => state.board.id);
  const { autoFetch } = useAppContext();
  
  // Sử dụng hook search từ Nav.js
  const {
    searchText,
    setSearchText,
    searchResults,
    isEmpty,
    loading,
    clearSearch,
  } = useSearchUser(autoFetch);

  const searchRef = useRef();
  useOnClickOutside(searchRef, () => clearSearch());

  const handleAddMember = (selectedUser) => {
    // Kiểm tra xem user đã là member của board chưa
    const checkMember = boardMembers.find((m) => m.email === selectedUser.email);
    if (checkMember) {
      dispatch(
        openAlert({
          message: `${checkMember.name} đã là thành viên của bảng này!`,
          severity: "error",
        })
      );
      clearSearch();
      return;
    }

    // Kiểm tra xem user đã được thêm vào danh sách chưa
    const alreadyAdded = newMembers.find((m) => m.email === selectedUser.email);
    if (alreadyAdded) {
      dispatch(
        openAlert({
          message: `${selectedUser.name} đã được thêm vào danh sách!`,
          severity: "warning",
        })
      );
      clearSearch();
      return;
    }

    // Thêm user vào danh sách member mới
    setNewMembers((prev) => [...prev, selectedUser]);
    clearSearch();
  };

  // Xóa member gốc khỏi board
  const handleDeleteOriginalMember = async (memberId) => {
    await boardMemberRemove(boardId, memberId, dispatch);
  };

  // Xóa member mới khỏi danh sách chờ thêm
  const handleDeleteNewMember = (email) => {
    setNewMembers((prev) => prev.filter((member) => member.email !== email));
  };

  // Lưu danh sách member mới vào board
  const handleInviteClick = async () => {
    if (newMembers.length === 0) {
      dispatch(
        openAlert({
          message: "Vui lòng chọn ít nhất một thành viên để mời!",
          severity: "warning",
        })
      );
      return;
    }
    await boardMemberAdd(boardId, newMembers, dispatch);
    setNewMembers([]);
    clearSearch();
  };

  return (
    <Container>
      <SearchContainer>
        <div style={{ position: "relative", width: "100%", flex: "3" }} ref={searchRef}>
          <SearchBar
            type="text"
            placeholder="Tìm kiếm người dùng..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
          />
          {/* Dropdown kết quả search */}
          {(isEmpty || searchResults.length > 0) && searchText && (
            <div
              style={{
                position: "relative",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1000,
                marginTop: "4px",
                maxHeight: "200px",
                overflowY: "auto",
                backgroundColor: "white",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              {isEmpty && (
                <div
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    color: "#666",
                  }}
                >
                  Không tìm thấy người dùng nào!
                </div>
              )}
              {searchResults.length > 0 && (
                <div>
                  {searchResults.map((resultUser) => {
                    // Lọc bỏ các user đã là member hoặc đã được thêm vào danh sách mới
                    const isBoardMember = boardMembers.some(
                      (m) => m.email === resultUser.email
                    );
                    const isAlreadyAdded = newMembers.some(
                      (m) => m.email === resultUser.email
                    );
                    
                    if (isBoardMember || isAlreadyAdded) {
                      return null;
                    }

                    return (
                      <div
                        key={resultUser._id}
                        onClick={() => handleAddMember(resultUser)}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          borderBottom: "1px solid #eee",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "white";
                        }}
                      >
                        <Avatar
                          src={resultUser.image?.url}
                          style={{ width: 32, height: 32 }}
                        >
                          {resultUser.name?.[0]}
                        </Avatar>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: "14px" }}>
                            {resultUser.name} ({resultUser.username})
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            {resultUser.email}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {loading && (
            <div
              style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <ReactLoading
                type="spinningBubbles"
                width={16}
                height={16}
                color="#0079bf"
              />
            </div>
          )}
        </div>
      </SearchContainer>
      
      {/* Hiển thị member gốc (có thể xóa) */}
      {boardMembers.length > 0 && (
        <>
          <div style={{ fontSize: "12px", color: "#666", marginTop: "8px", marginBottom: "4px" }}>
            Thành viên hiện tại:
          </div>
          <ChipContainer>
            {boardMembers.map((member) => {
              return (
                <OriginalMemberChip
                  key={member._id}
                  callback={handleDeleteOriginalMember}
                  {...member}
                />
              );
            })}
          </ChipContainer>
        </>
      )}

      {/* Hiển thị member mới (cần add) */}
      {newMembers.length > 0 && (
        <>
          <div style={{ fontSize: "12px", color: "#666", marginTop: "8px", marginBottom: "4px" }}>
            Thành viên sẽ được thêm:
          </div>
          <ChipContainer>
            {newMembers.map((member) => {
              return (
                <NewMemberChip
                  key={member.email}
                  callback={handleDeleteNewMember}
                  name={member.name}
                  email={member.email}
                  image={member.image}
                />
              );
            })}
          </ChipContainer>
        </>
      )}

      {/* Chỉ hiện nút Lưu khi có member mới cần add */}
      {newMembers.length > 0 && (
        <Button clickCallback={handleInviteClick} title="Thêm" />
      )}
    </Container>
  );
};

export default InviteMembers;
